import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Check,
  X,
  ArrowRight,
  Zap,
  Users,
  Building,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { api } from '../context/AuthContext';

// Fallback until /billing/plans loads (config/plans.py is the source of truth: annual = 10x monthly)
const FALLBACK_PRICES = {
  base: { price: 32, annual_price: 320 },
  standard: { price: 64, annual_price: 640 },
  advanced: { price: 131, annual_price: 1310 },
};

const PricingPage = () => {
  const [annual, setAnnual] = useState(true);
  const [prices, setPrices] = useState(FALLBACK_PRICES);

  useEffect(() => {
    api.get('/billing/plans').then((res) => {
      if (res.data?.plans) setPrices((prev) => ({ ...prev, ...res.data.plans }));
    }).catch(() => {});
  }, []);

  const priceFor = (planId) => (annual
    ? Math.round(prices[planId].annual_price / 12)
    : prices[planId].price);

  const plans = [
    {
      name: 'Base',
      description: 'Perfect for individuals and small projects',
      price: priceFor('base'),
      annualPrice: prices.base.annual_price,
      icon: Zap,
      color: 'emerald',
      features: [
        { text: '2 Calendars', included: true },
        { text: '2 Users', included: true },
        { text: '10 Social Accounts', included: true },
        { text: 'Basic Analytics', included: true },
        { text: 'Content Calendar', included: true },
        { text: '150 AI Credits / month', included: true },
        { text: 'All AI Tools: Captions, Ideas, Images & Video', included: true },
        { text: 'Team Collaboration', included: false },
        { text: 'Custom Reports', included: false },
        { text: 'API Access', included: false },
        { text: 'Custom Webhooks', included: false }
      ]
    },
    {
      name: 'Standard',
      description: 'For growing businesses and teams',
      price: priceFor('standard'),
      annualPrice: prices.standard.annual_price,
      icon: Users,
      color: 'blue',
      popular: true,
      features: [
        { text: '6 Calendars', included: true },
        { text: '6 Users', included: true },
        { text: '10 Social Accounts', included: true },
        { text: 'Advanced Analytics', included: true },
        { text: 'Approval Workflows', included: true },
        { text: 'Team Collaboration', included: true },
        { text: '500 AI Credits / month', included: true },
        { text: 'All AI Tools: Captions, Ideas, Images & Video', included: true },
        { text: 'Custom Reports', included: true },
        { text: 'API Access', included: false },
        { text: 'Custom Webhooks', included: false }
      ]
    },
    {
      name: 'Advanced',
      description: 'For large organizations with custom needs',
      price: priceFor('advanced'),
      annualPrice: prices.advanced.annual_price,
      icon: Building,
      color: 'purple',
      features: [
        { text: '14 Calendars', included: true },
        { text: '14 Users', included: true },
        { text: '10 Social Accounts', included: true },
        { text: '1,500 AI Credits / month', included: true },
        { text: 'All AI Tools: Captions, Ideas, Images & Video', included: true },
        { text: 'Custom Branding', included: true },
        { text: 'Custom Reports', included: true },
        { text: 'Full API Access', included: true },
        { text: 'Custom Webhooks', included: true },
        { text: 'Priority Support', included: true }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-emerald-600 font-medium">Pricing</Link>
              <Link to="/integrations" className="text-slate-600 hover:text-slate-900">Integrations</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-emerald-100 text-emerald-700 mb-4">Pricing</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Simple, Transparent
            <span className="text-emerald-500"> Pricing</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Choose the plan that fits your needs. All plans include a 14-day free trial.
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm ${!annual ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <span className={`text-sm ${annual ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
              Annual <Badge variant="secondary" className="ml-1">2 months free</Badge>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className={`h-full relative ${plan.popular ? 'border-emerald-500 border-2 shadow-lg' : 'border-slate-200'}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-emerald-500 text-white">Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                      plan.color === 'emerald' ? 'bg-emerald-100' :
                      plan.color === 'blue' ? 'bg-blue-100' : 'bg-purple-100'
                    }`}>
                      <plan.icon className={`w-6 h-6 ${
                        plan.color === 'emerald' ? 'text-emerald-600' :
                        plan.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                      }`} />
                    </div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-slate-900">${plan.price}</span>
                      <span className="text-slate-500">/month</span>
                      <p className="text-xs text-slate-500 mt-1">
                        {annual ? `$${plan.annualPrice.toLocaleString()} billed yearly` : 'billed monthly'}
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Link to="/register">
                      <Button className={`w-full mb-6 ${
                        plan.popular ? 'bg-emerald-500 hover:bg-emerald-600' : ''
                      }`} variant={plan.popular ? 'default' : 'outline'}>
                        Start Free Trial
                      </Button>
                    </Link>
                    <ul className="space-y-3">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-3">
                          {feature.included ? (
                            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                          )}
                          <span className={feature.included ? 'text-slate-700' : 'text-slate-400'}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              { q: 'Can I change plans at any time?', a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.' },
              { q: 'What happens after my free trial?', a: 'After your 14-day trial, you\'ll be asked to choose a plan. No charges during the trial period.' },
              { q: 'Do you offer refunds?', a: 'Yes, we offer a 30-day money-back guarantee on all plans.' },
              { q: 'Can I add more users later?', a: 'Absolutely! You can add users to your plan at any time. Additional users are prorated.' },
              { q: 'Is there a contract or commitment?', a: 'No long-term contracts. Monthly plans can be cancelled anytime. Annual plans are billed yearly.' }
            ].map((faq, i) => (
              <Card key={i} className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
